import {
  addDays,
  addHours,
  differenceInHours,
  endOfDay,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { DateFormat, i18nFormat } from 'utils/date.utils';
import { notEmpty } from 'utils/typescript.utils';

import { EVENT_DAY_HOUR_OFFSET, SiteLocale } from 'constants/i18n.constants';
import { PageEventFragment } from 'types/api/dato-operations.types';

interface EventDateTimeProps {
  startDatetime: string | undefined;
  endDatetime: string | undefined;
  timezone: string;
}

export const processDateTime = (
  startDate: string,
  endDate: string,
  timeZone: string,
) => {
  const zonedStartDate = toZonedTime(new Date(startDate), timeZone);
  const zonedEndDate = toZonedTime(new Date(endDate), timeZone);

  const endHour = zonedEndDate.getHours() + zonedEndDate.getMinutes() / 60;

  const updatedZonedEndDate =
    endHour <= EVENT_DAY_HOUR_OFFSET ? addDays(zonedEndDate, -1) : zonedEndDate;

  const longerThanADay = differenceInHours(zonedStartDate, zonedEndDate) >= 24;

  const sameDayAsStart = isSameDay(zonedStartDate, updatedZonedEndDate);

  const isSingleDay = !longerThanADay && sameDayAsStart;

  return {
    zonedStartDate,
    updatedZonedEndDate,
    isSingleDay,
  };
};

export const isEventToday = ({
  startDatetime,
  endDatetime,
  timezone,
}: EventDateTimeProps) => {
  if (!startDatetime || !endDatetime) return false;

  const zonedStartDate = startOfDay(
    toZonedTime(new Date(startDatetime), timezone),
  );
  const zonedEndDate = addHours(
    endOfDay(toZonedTime(new Date(endDatetime), timezone)),
    EVENT_DAY_HOUR_OFFSET,
  );
  const currentDate = toZonedTime(new Date(), timezone);

  return currentDate >= zonedStartDate && currentDate <= zonedEndDate;
};

export const isUpcomingEvent = ({
  startDatetime,
  endDatetime,
  timezone,
}: EventDateTimeProps) => {
  if (!startDatetime || !endDatetime) return false;

  const zonedEndDate = toZonedTime(new Date(endDatetime), timezone);
  const currentDate = toZonedTime(new Date(), timezone);

  return currentDate < zonedEndDate;
};

export const isPastEvent = ({
  startDatetime,
  endDatetime,
  timezone,
}: EventDateTimeProps) => {
  if (!startDatetime || !endDatetime) return false;
  const zonedEndDate = toZonedTime(new Date(endDatetime), timezone);
  const currentDate = toZonedTime(new Date(), timezone);

  return currentDate > zonedEndDate;
};

export const sortByStartDateTime = (
  a: { startDatetime: string | undefined; timezone: string },
  b: { startDatetime: string | undefined; timezone: string },
  sortDirection: 'asc' | 'desc' = 'desc',
) => {
  const zonedA = a.startDatetime
    ? toZonedTime(new Date(a.startDatetime), a.timezone)
    : undefined;
  const zonedB = b.startDatetime
    ? toZonedTime(new Date(b.startDatetime), b.timezone)
    : undefined;

  const compareResult =
    (zonedA ? new Date(zonedA) : 0) > (zonedB ? new Date(zonedB) : 0) ? 1 : -1;

  return sortDirection === 'asc' ? compareResult : -compareResult;
};

export const sortByEndDateTime = (
  a: { endDatetime: string | undefined; timezone: string },
  b: { endDatetime: string | undefined; timezone: string },
) => {
  const zonedA = a.endDatetime
    ? toZonedTime(new Date(a.endDatetime), a.timezone)
    : undefined;

  const zonedB = b.endDatetime
    ? toZonedTime(new Date(b.endDatetime), b.timezone)
    : undefined;

  const compareResult =
    (zonedA ? new Date(zonedA) : 0) > (zonedB ? new Date(zonedB) : 0) ? 1 : -1;

  return compareResult;
};

type FormatEventDateProps = {
  locale: SiteLocale;
  startDate?: string;
  endDate?: string;
  timeZone: string;
  isPastEvent?: boolean;
};

export const formatEventDate = ({
  locale,
  startDate,
  endDate,
  timeZone,
  isPastEvent = false,
}: FormatEventDateProps) => {
  if (!startDate || !endDate) return null;

  const dateFormat = isPastEvent
    ? DateFormat.LongDayDate[locale]
    : DateFormat.ShortDayDate[locale];

  const { zonedStartDate, updatedZonedEndDate, isSingleDay } = processDateTime(
    startDate,
    endDate,
    timeZone,
  );

  return isSingleDay
    ? i18nFormat(zonedStartDate, dateFormat, locale)
    : [
        i18nFormat(
          zonedStartDate,
          DateFormat.ShortWithoutDayDate[locale],
          locale,
        ),
        i18nFormat(
          updatedZonedEndDate,
          DateFormat.ShortWithoutDayDate[locale],
          locale,
        ),
      ]
        .filter(notEmpty)
        .filter(Boolean)
        .join(' - ');
};

type PageEventWithTicketing = PageEventFragment & {
  ticketsIntegration: 'ticketing_platform';
  ticketsEventId: string;
};

export const isEventTicketingEnabled = (
  event: PageEventFragment | undefined,
): event is PageEventWithTicketing =>
  !!event &&
  event.ticketsIntegration === 'ticketing_platform' &&
  !!event.ticketsEventId;
